// client/src/user/dashboard/chat/CodeBlock.js
import React, { useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

const CodeBlock = ({ language, value }) => {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const displayLang = language || "text";
  const lineCount = value.split("\n").length;
  const isLongCode = lineCount > 18;

  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        boxShadow: isDark
          ? `0 4px 20px ${alpha(theme.palette.common.black, 0.35)}`
          : `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          maxHeight: isLongCode ? { xs: 420, sm: 520 } : "none",
          overflow: isLongCode ? "auto" : "visible",
          bgcolor: isDark
            ? alpha(theme.palette.common.black, 0.22)
            : alpha(theme.palette.common.black, 0.035),
          "&::-webkit-scrollbar": {
            width: 6,
            height: 6,
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: alpha(theme.palette.text.primary, 0.04),
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(theme.palette.text.primary, 0.18),
            borderRadius: 2,
            "&:hover": {
              bgcolor: alpha(theme.palette.text.primary, 0.28),
            },
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1.25, sm: 2 },
            py: 0.75,
            bgcolor: alpha(
              theme.palette.background.paper,
              isDark ? 0.92 : 0.96,
            ),
            borderBottom: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(10px)",
            minWidth: 0,
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "text.secondary",
              fontFamily: '"JetBrains Mono", monospace',
              textTransform: "lowercase",
              letterSpacing: "0.04em",
              minWidth: 0,
            }}
          >
            {displayLang}
            {isLongCode ? ` · ${lineCount} lines` : ""}
          </Typography>

          <Tooltip title={copied ? "Copied!" : "Copy code"}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                color: copied ? "success.main" : "text.secondary",
                p: 0.5,
                borderRadius: 1,
                gap: 0.5,
                fontSize: "0.72rem",
                fontWeight: 600,
                flexShrink: 0,
                bgcolor: copied
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.text.primary, 0.04),
                "&:hover": {
                  bgcolor: copied
                    ? alpha(theme.palette.success.main, 0.14)
                    : "action.hover",
                  color: copied ? "success.main" : "text.primary",
                },
                transition: "all 0.15s",
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 14 }} />
              ) : (
                <CopyIcon sx={{ fontSize: 14 }} />
              )}

              <Typography
                component="span"
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "inherit",
                  lineHeight: 1,
                  display: { xs: "none", sm: "inline" },
                }}
              >
                {copied ? "Copied!" : "Copy"}
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            width: "max-content",
            minWidth: "100%",
          }}
        >
          <SyntaxHighlighter
            language={displayLang}
            style={oneDark}
            showLineNumbers={lineCount > 4}
            wrapLongLines={false}
            customStyle={{
              margin: 0,
              padding: "16px",
              background: "transparent",
              fontSize: "0.83rem",
              lineHeight: "1.65",
              fontFamily:
                '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
              borderRadius: 0,
              overflow: "visible",
              minWidth: "max-content",
            }}
            lineNumberStyle={{
              color: alpha(theme.palette.text.primary, 0.35),
              fontSize: "0.75rem",
              minWidth: "2.5em",
              paddingRight: "1em",
              userSelect: "none",
            }}
            codeTagProps={{
              style: {
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              },
            }}
          >
            {value}
          </SyntaxHighlighter>
        </Box>
      </Box>
    </Box>
  );
};

export default CodeBlock;
